import { Button, PageError, PageHeader, PageLoading } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Input } from "@majitap/ui/arc/input/input";
import { RadioCards } from "@majitap/ui/arc/radio-cards/radio-cards";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Switch } from "@majitap/ui/arc/switch/switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { messageFor } from "../../error-copy";
import { useFormCheck } from "../../form-check";
import { SETTINGS_MENU } from "../../nav";
import { type EtaSettings, etaSettingsQuery } from "../../tracking";
import styles from "./invoice-details.module.css";

const item = SETTINGS_MENU.flatMap((s) => s.items).find((i) => i.id === "delivery-eta")!;

type Source = EtaSettings["source"];
export type Values = {
  show_customers: boolean;
  source: Source;
  speed: "learned" | "fixed";
  fixed_speed_kmh: string;
  road_factor: string;
  stale_after_minutes: string;
  late_after_minutes: string;
};

const valuesOf = (s: EtaSettings): Values => ({
  show_customers: s.show_customers,
  source: s.source,
  speed: s.fixed_speed_kmh === null ? "learned" : "fixed",
  fixed_speed_kmh: s.fixed_speed_kmh === null ? "" : String(s.fixed_speed_kmh),
  road_factor: s.road_factor,
  stale_after_minutes: String(s.stale_after_minutes),
  late_after_minutes: String(s.late_after_minutes),
});

const whole = (v: string, min: number, max: number) => {
  if (!/^\d+$/.test(v.trim())) return null;
  // number-ok: minutes and km/h, not money
  const n = Number.parseInt(v, 10);
  return n >= min && n <= max ? n : null;
};

/** The road factor as the API takes it, "1.30", or null when not 1.00 to 3.00. */
const factorOf = (v: string) => {
  if (!/^\d(\.\d{1,2})?$/.test(v.trim())) return null;
  // number-ok: a ratio, not money
  const n = Number(v);
  // number-ok: a ratio, not money
  return n >= 1 && n <= 3 ? n.toFixed(2) : null;
};

/** Each field's problem, in the ranges of the eta_settings CHECK (migration 20261102180100_eta_settings). */
const errorsOf = (v: Values) => ({
  fixed_speed_kmh:
    v.speed === "learned" || whole(v.fixed_speed_kmh, 5, 120) !== null
      ? undefined
      : "Enter a whole number of km/h from 5 to 120.",
  road_factor:
    factorOf(v.road_factor) !== null ? undefined : "Enter a factor from 1 to 3, with up to 2 decimals, like 1.3.",
  stale_after_minutes: whole(v.stale_after_minutes, 1, 60) !== null ? undefined : "Enter whole minutes from 1 to 60.",
  late_after_minutes: whole(v.late_after_minutes, 0, 120) !== null ? undefined : "Enter whole minutes from 0 to 120.",
});

/** What changed, or null while a field is out of range. */
export function changesOf(current: EtaSettings, v: Values): Partial<EtaSettings> | null {
  const speed = v.speed === "learned" ? null : whole(v.fixed_speed_kmh, 5, 120);
  const factor = factorOf(v.road_factor);
  const stale = whole(v.stale_after_minutes, 1, 60);
  const late = whole(v.late_after_minutes, 0, 120);
  if ((v.speed === "fixed" && speed === null) || factor === null || stale === null || late === null) return null;
  const next = {
    show_customers: v.show_customers,
    source: v.source,
    fixed_speed_kmh: speed,
    road_factor: factor,
    stale_after_minutes: stale,
    late_after_minutes: late,
  };
  return Object.fromEntries(Object.entries(next).filter(([k, value]) => current[k as keyof EtaSettings] !== value));
}

/**
 * Settings › Operations › Delivery ETA (P8-73): whether customers see how long a delivery on its
 * way will take, Google's drive time with traffic or the company's own estimate, the estimate's
 * speed and road factor, and when to hide it or call it late. Needs `fleet.manage`; audited.
 */
export function DeliveryEtaPage(): ReactNode {
  const settings = useQuery(etaSettingsQuery);
  if (settings.status === "pending")
    return (
      <>
        <PageHeader title={item.label} description={item.description} />
        <PageLoading label="Loading the ETA settings" />
      </>
    );
  if (settings.status === "error")
    return (
      <>
        <PageHeader title={item.label} description={item.description} />
        <PageError message={messageFor(errorOf(settings.error))} onRetry={() => settings.refetch()} />
      </>
    );
  return <EtaForm current={settings.data} />;
}

const FORM_ID = "delivery-eta";

function EtaForm({ current }: { current: EtaSettings }) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState(() => valuesOf(current));
  const changes = changesOf(current, values);
  const changed = changes !== null && Object.keys(changes).length > 0;
  // nothing to save: every field is valid and as saved
  const unchanged = changes !== null && !changed;
  const check = useFormCheck();
  const errors = errorsOf(values);
  const save = useMutation({
    mutationFn: () => ok(api.PUT("/v1/admin/tracking/eta-settings", { body: changes ?? {} })),
    onSuccess: (saved) => queryClient.setQueryData(etaSettingsQuery.queryKey, saved),
  });
  const set = (patch: Partial<Values>) => {
    save.reset();
    setValues((v) => ({ ...v, ...patch }));
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    check.submit(errors, () => {
      if (changed) save.mutate();
    });
  };
  const learned =
    current.learned_speed_kmh === null
      ? `Not enough trips yet: ${current.learned_trips} in the last 30 days, 10 needed. Until then, 25 km/h.`
      : `${current.learned_speed_kmh} km/h over the last 30 days, from ${current.learned_trips} deliveries.`;
  return (
    <>
      <PageHeader
        title={item.label}
        description={item.description}
        actions={
          <Button type="submit" form={FORM_ID} disabled={unchanged} loading={save.isPending}>
            Save ETA settings
          </Button>
        }
      />
      {save.error ? (
        <Alert tone="danger" title="Not saved">
          {messageFor(errorOf(save.error))}
        </Alert>
      ) : null}
      {save.isSuccess && !changed ? (
        <Alert tone="success" title="Saved">
          Dispatch, delivery pages and the customer app use these from their next update.
        </Alert>
      ) : null}
      <div className={styles.columns}>
        <form id={FORM_ID} onSubmit={submit} aria-label="Delivery ETA">
          <section className={styles.section}>
            <div className={styles.head}>
              <h2 className={styles.title}>Who sees it</h2>
              <p className={styles.note}>Staff always see it on Dispatch and the delivery's page.</p>
            </div>
            <div className={styles.fields}>
              <Switch
                label="Show customers the ETA"
                checked={values.show_customers}
                onCheckedChange={(on) => set({ show_customers: on })}
              />
              <p className={styles.note}>
                In the app and the business API, while the truck is on its way: "Arriving in about 25 min".
              </p>
            </div>
          </section>
          <section className={styles.section}>
            <div className={styles.head}>
              <h2 className={styles.title}>How it's worked out</h2>
              <p className={styles.note}>From the truck's latest position to the delivery's place.</p>
            </div>
            <div className={styles.fields}>
              <RadioCards
                aria-label="How it's worked out"
                layout="list"
                value={values.source}
                onValueChange={(v) => set({ source: v as Source })}
                options={[
                  {
                    value: "google",
                    label: "Google, with traffic",
                    description: current.google_connected
                      ? "Drive time on the roads now. If Google doesn't answer, your own estimate is used. Connected."
                      : "Drive time on the roads now. Not connected yet: add Google Routes in Integrations; until then, your own estimate is used.",
                  },
                  {
                    value: "estimate",
                    label: "Your own estimate",
                    description:
                      "From your trucks' usual speed and how much longer the roads are than a straight line. No cost.",
                  },
                ]}
              />
            </div>
          </section>
          <section className={styles.section}>
            <div className={styles.head}>
              <h2 className={styles.title}>Your own estimate</h2>
              <p className={styles.note}>Used when Google isn't connected or doesn't answer.</p>
            </div>
            <div className={styles.fields}>
              <div>
                <p className={styles.legend}>Average speed on the way</p>
                <SegmentedControl
                  label="Average speed on the way"
                  value={values.speed}
                  onValueChange={(v) => set({ speed: v as Values["speed"] })}
                  options={[
                    { value: "learned", label: "Learned from your trucks" },
                    { value: "fixed", label: "Fixed" },
                  ]}
                />
              </div>
              {values.speed === "learned" ? (
                <p className={styles.note}>{learned}</p>
              ) : (
                <Input
                  className={styles.narrowField}
                  label="Speed (km/h)"
                  description="5 to 120."
                  inputMode="numeric"
                  value={values.fixed_speed_kmh}
                  onChange={(e) => set({ fixed_speed_kmh: e.target.value })}
                  error={check.show(errors.fixed_speed_kmh)}
                />
              )}
              <Input
                className={styles.narrowField}
                label="Road factor"
                description="How much longer the roads are than a straight line: 1.3 is 30% longer. 1 to 3."
                inputMode="decimal"
                value={values.road_factor}
                onChange={(e) => set({ road_factor: e.target.value })}
                error={check.show(errors.road_factor)}
              />
            </div>
          </section>
          <section className={styles.section}>
            <div className={styles.head}>
              <h2 className={styles.title}>When to hide or warn</h2>
              <p className={styles.note}>A guess from an old position is worse than none.</p>
            </div>
            <div className={styles.row}>
              <Input
                label="Hide it after no position for"
                description="Minutes, 1 to 60. Then customers see the truck's last update instead."
                inputMode="numeric"
                value={values.stale_after_minutes}
                onChange={(e) => set({ stale_after_minutes: e.target.value })}
                error={check.show(errors.stale_after_minutes)}
              />
              <Input
                label="Running late after the promise by"
                description="Minutes, 0 to 120. 0 is as soon as the ETA passes the promised time."
                inputMode="numeric"
                value={values.late_after_minutes}
                onChange={(e) => set({ late_after_minutes: e.target.value })}
                error={check.show(errors.late_after_minutes)}
              />
            </div>
          </section>
          <p className={styles.audit}>
            <Lock size={16} aria-hidden="true" />
            Only fleet managers can change these. Every change is in the Audit log, with what it was before.
          </p>
          <div className={styles.footer}>
            <Button type="submit" disabled={unchanged} loading={save.isPending}>
              Save ETA settings
            </Button>
          </div>
        </form>
        <Example values={values} />
      </div>
    </>
  );
}

/** What customers see on the way, for a truck 9 km off with Google saying 23 minutes. */
function Example({ values: v }: { values: Values }) {
  return (
    <aside className={styles.preview} aria-label="What customers see">
      <div className={styles.previewHead}>
        <h2 className={styles.title}>What customers see</h2>
        <span className={styles.note}>Example</span>
      </div>
      {v.show_customers ? (
        <div className={styles.paper}>
          <div className={styles.paperBody}>
            <span className={styles.paperLabel}>ON THE WAY</span>
            <strong className={styles.paperHeadline}>Arriving in about 25 min</strong>
            <span className={styles.paperMuted}>Around 14:35 · 9 km by road · updated 1 min ago</span>
          </div>
        </div>
      ) : (
        <p className={styles.note}>
          Customers see where the truck is and when it last updated, without an ETA. Staff still see it.
        </p>
      )}
      <p className={styles.note}>
        Late by the promise: "Running late · about 40 min". With no position for {v.stale_after_minutes || "5"} minutes:
        "Last seen 3 km away · 6 min ago".
      </p>
    </aside>
  );
}
